import {isPlatformBrowser} from '@angular/common';
import {HttpClient, HttpStatusCode} from '@angular/common/http';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Router} from '@angular/router';
import {ICountrySelectorService, JsonService, LocalStorageService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {
  AuthService,
  CmsActions,
  Country,
  OccEndpointsService,
  Product,
  ProductActions,
  RoutingService,
  SiteConnector,
  User,
} from '@spartacus/core';
import {CurrentProductService} from '@spartacus/storefront';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {debounceTime, filter, first, map} from 'rxjs/operators';
import {CountrySwitchModalComponent} from '../layout/header/country-selector/country-switch-modal/country-switch-modal.component';
import {SkipInterception} from '../model/response-status.model';

interface CountrySelectorState {
  currentCountry: Country;
}

export interface IsoCodeState {
  isoCode: string;
}

export const DEFAULT_COUNTRY_ISO_CODE: string = 'US';
export const DEFAULT_COUNTRY_NAME: string = 'United States';
export const DEFAULT_COUNTRY_SELECTOR_COUNTRY: Country = {
  isocode: DEFAULT_COUNTRY_ISO_CODE,
  name: DEFAULT_COUNTRY_NAME,
};

export enum CountrySelectorStatus {
  CONFIRM_AND_VISIT_HOMEPAGE = 'confirmAndVisitHomepage',
  WITHDRAW_COUNTRY_SELECTOR = 'withdrawCountrySelection',
  PRODUCT_ALLOWED = 'productAllowed',
  NO_PRODUCT_PAGE = 'noProductPage',
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CountrySelectorService implements ICountrySelectorService {
  countryChangeSubject = new Subject<IsoCodeState>();
  private storageKey = 'countrySelectorState';
  private countrySelectorState: CountrySelectorState;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private store: Store<{}>,
    private routingService: RoutingService,
    private occEndpointsService: OccEndpointsService,
    private currentProductService: CurrentProductService,
    private httpClient: HttpClient,
    private siteConnector: SiteConnector,
    private localStorageService: LocalStorageService,
    private jsonService: JsonService,
    private authService: AuthService,
    private userFacade: UserAccountFacade,
    private router: Router
  ) {
    // Set Default for Current Country
    this.countrySelectorState = {
      currentCountry: DEFAULT_COUNTRY_SELECTOR_COUNTRY,
    };

    this.setUserCountryOnLogin();

    this.loadStateFromStorage();
  }

  /**
   * Calls the API to check if the product is available in the current country
   *
   * @param countryIsocode iso code of the country eg. BR for Brazil
   * @param productCode code of the product
   * @returns the observable of the http request to the product occ api
   */
  public isProductAvailableInCountry(countryIsocode: string, productCode: string): Promise<boolean> {
    const apiUrl: string = this.occEndpointsService.buildUrl('/products/' + productCode);
    return new Promise<boolean>((resolve, reject) => {
      this.httpClient
        .get<any>(apiUrl, {
          headers: {
            country: countryIsocode,
            'Cache-Control': 'no-cache',
          },
          params: {
            skipInterceptor: SkipInterception.UNKNOWN_IDENTIFIER_ERROR,
          },
          observe: 'response',
        }) // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        .subscribe({
          next: () => resolve(true),
          error: (resp) => resolve(resp.status !== HttpStatusCode.BadRequest),
        });
    });
  }

  public getCurrentCountry(): Country {
    return this.countrySelectorState.currentCountry;
  }

  public getCountries(): Observable<Country[]> {
    return this.siteConnector.getCountries();
  }

  public setCurrentUserCountry(user: User): void {
    this.setCurrentCountry(this.getCountryByUser(user));
  }

  /**
   * Returns the Country on the user address.
   *
   * @param user to determine the country by.
   */
  public getCountryByUser(user: User): Country {
    if (user.defaultUnitAddress && user.defaultUnitAddress.country) {
      return user.defaultUnitAddress.country;
    } else if (user.defaultAddress && user.defaultAddress.country) {
      return user.defaultAddress.country;
    }
    return DEFAULT_COUNTRY_SELECTOR_COUNTRY;
  }

  /**
   * Sets the new Country and checks for if the switch is allowed.
   *
   * @param newCountry Country to set the country selector to.
   * It needs to be attached if you want to show a waring when the user is on a product page,
   * that must not be shown in the country he switches to.
   */
  public setCurrentCountry(newCountry: Country): void {
    if (this.countrySelectorState.currentCountry === newCountry) {
      return;
    }

    this.countrySelectorState.currentCountry = newCountry;
    this.saveStateToStorage();
    combineLatest([this.routingService.getRouterState(), this.routingService.getPageContext()])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([routerState, pageContext]) => {
        if (pageContext?.id) {
          this.store.dispatch(new CmsActions.LoadCmsPageData(pageContext));
        }

        this.store.dispatch(
          new ProductActions.SearchProducts({
            queryText: routerState.state.queryParams?.query || routerState.state.params.query || '',
            searchConfig: {
              currentPage: 0,
              pageSize: 18,
            },
          })
        );
        this.sendChangeNotification(newCountry.isocode);
      });
  }

  /**
   * Starts a listener that checks if the user is changing the country on a product page.
   * It will show a warning when the product is not supported in the country the user switching to.
   */
  public getCountrySwitchStatus(
    currentCountryIsocode: string,
    countrySwitchModalComponentRef: CountrySwitchModalComponent
  ): Observable<Promise<string>> {
    return this.getCurrentProduct().pipe(
      map((currentProduct) => {
        if (currentProduct) {
          return countrySwitchModalComponentRef.confirmCountryChange(currentCountryIsocode, currentProduct);
        } else {
          return new Promise<string>((resolve) => {
            resolve(CountrySelectorStatus.NO_PRODUCT_PAGE);
          });
        }
      })
    );
  }

  public initializeCountrySwitchWarning(
    currentCountryIsocode: string,
    countrySwitchModalComponentRef: CountrySwitchModalComponent
  ): void {
    this.getCurrentProduct()
      .pipe(
        untilDestroyed(this),
        map((currentProduct) => {
          if (currentProduct) {
            return countrySwitchModalComponentRef.confirmCountryChange(currentCountryIsocode, currentProduct);
          } else {
            return new Promise<string>((resolve) => {
              resolve(CountrySelectorStatus.NO_PRODUCT_PAGE);
            });
          }
        })
      )
      .subscribe((promise) =>
        promise.then((status) => {
          this.handleCountryConfirmation(status, currentCountryIsocode);
        })
      );
  }

  /**
   * Checks if a product page is displayed at the moment and returns the product.
   *
   * @return the product of the currently visited product page. If the user it not on a product page null is returned.
   */
  public getCurrentProduct(): Observable<Product> {
    if (this.router.url?.includes('/product/')) {
      return this.currentProductService.getProduct();
    } else {
      return of(null);
    }
  }

  /**
   * This causes a getProduct request.
   * It should be called as soon as the country is switched on a product page.
   */
  reloadProduct(): void {
    this.currentProductService
      .getProduct()
      .pipe(untilDestroyed(this))
      .subscribe((product: Product) => {
        if (product) {
          this.store.dispatch(new ProductActions.LoadProduct(product.code));
        }
      });
  }

  /**
   * Notify other country selector components currently displayed.
   *
   * @param isoCode the other components receive.
   */
  sendChangeNotification(isoCode: string) {
    this.countryChangeSubject.next({isoCode});
  }

  /**
   * Listen to notifications from other country selector components currently displayed.
   */
  getChangeNotification(): Observable<IsoCodeState> {
    return this.countryChangeSubject.asObservable();
  }

  /**
   * Performs an action according to a status of country selector change,
   *
   * @param status
   * @param isocode
   * @private
   */
  private handleCountryConfirmation(status: string, isocode: string): void {
    switch (status) {
      case CountrySelectorStatus.CONFIRM_AND_VISIT_HOMEPAGE:
        this.setCurrentCountry({isocode});
        this.sendChangeNotification(isocode);
        break;
      case CountrySelectorStatus.PRODUCT_ALLOWED:
      case CountrySelectorStatus.NO_PRODUCT_PAGE:
        this.setCurrentCountry({isocode});
        this.sendChangeNotification(isocode);
        this.reloadProduct();
        this.reloadPage();
        break;
      case CountrySelectorStatus.WITHDRAW_COUNTRY_SELECTOR:
        this.reloadPage();
        break;
    }
  }

  private reloadPage(): void {
    if (isPlatformBrowser(this.platformId) && window?.location) {
      window.location.reload();
    }
  }

  private loadStateFromStorage(): void {
    try {
      const persistentState: object = this.jsonService.parseSafely(this.localStorageService.getItem(this.storageKey));
      if (persistentState && typeof persistentState === 'object' && Object.keys(persistentState).length > 0) {
        this.countrySelectorState = {...this.countrySelectorState, ...persistentState};
      }
    } catch (e) {}
  }

  private saveStateToStorage(): void {
    this.localStorageService.setItem(this.storageKey, JSON.stringify(this.countrySelectorState));
  }

  public hasStateFromStorage(): boolean {
    return this.localStorageService.getItem(this.storageKey) !== null;
  }

  private setUserCountryOnLogin(): void {
    combineLatest([
      this.userFacade.get().pipe(
        filter((user) => this.isUserDefaultUnitAddressLoaded(user)),
        debounceTime(200)
      ),
      this.authService.isUserLoggedIn().pipe(filter(Boolean)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([user]) => {
        this.setCurrentUserCountry(user);
      });
  }

  /**
   * Check if fields required for country determination are loaded.
   * Only users with defaultUnitAddress
   * should be able to set their country to country-selector
   *
   * @param user whose loading is going to be checked
   */
  private isUserDefaultUnitAddressLoaded(user: User): boolean {
    return user?.hasOwnProperty('defaultUnitAddress');
  }
}
